<template>
  <ContentWrap>
    <el-alert
      class="mb-15px"
      type="info"
      :closable="false"
      title="平台台账：查看全部租户的 OSS 容量购买订单。租户侧仅能在「OSS容量扩容」看到本户订单。"
    />
    <el-form class="-mb-15px" :model="queryParams" :inline="true" label-width="80px">
      <el-form-item label="租户编号" prop="tenantId">
        <el-input
          v-model="queryParams.tenantId"
          placeholder="租户 ID"
          clearable
          class="!w-160px"
          @keyup.enter="handleQuery"
        />
      </el-form-item>
      <el-form-item label="支付状态" prop="payStatus">
        <el-select v-model="queryParams.payStatus" placeholder="全部" clearable class="!w-140px">
          <el-option label="待支付" :value="0" />
          <el-option label="已支付" :value="10" />
          <el-option label="已关闭" :value="30" />
        </el-select>
      </el-form-item>
      <el-form-item label="下单时间" prop="createTime">
        <el-date-picker
          v-model="queryParams.createTime"
          value-format="YYYY-MM-DD HH:mm:ss"
          type="daterange"
          start-placeholder="开始"
          end-placeholder="结束"
          :default-time="[new Date('1 00:00:00'), new Date('1 23:59:59')]"
          class="!w-240px"
        />
      </el-form-item>
      <el-form-item>
        <el-button @click="handleQuery"><Icon icon="ep:search" class="mr-5px" />搜索</el-button>
        <el-button @click="resetQuery"><Icon icon="ep:refresh" class="mr-5px" />重置</el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-table v-loading="loading" :data="list" stripe>
      <el-table-column label="订单号" prop="orderId" width="100" />
      <el-table-column label="租户" min-width="160">
        <template #default="{ row }">
          <div>{{ row.tenantName || '-' }}</div>
          <div class="text-secondary">ID {{ row.tenantId }}</div>
        </template>
      </el-table-column>
      <el-table-column label="套餐" prop="planName" min-width="140" />
      <el-table-column label="容量" min-width="100">
        <template #default="{ row }">{{ formatBytes(row.capacityBytes) }}</template>
      </el-table-column>
      <el-table-column label="金额" width="110" align="right">
        <template #default="{ row }">¥{{ ((row.amount || 0) / 100).toFixed(2) }}</template>
      </el-table-column>
      <el-table-column label="状态" width="100" align="center">
        <template #default="{ row }">
          <el-tag :type="payStatusTagType(row.payStatus)" size="small">
            {{ payStatusLabel(row.payStatus) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="支付单" prop="payOrderId" width="110" />
      <el-table-column label="支付时间" width="170">
        <template #default="{ row }">{{ formatOrderTime(row.paySuccessTime) }}</template>
      </el-table-column>
      <el-table-column label="下单时间" width="170">
        <template #default="{ row }">{{ formatOrderTime(row.createTime) }}</template>
      </el-table-column>
    </el-table>
    <Pagination
      :total="total"
      v-model:page="queryParams.pageNo"
      v-model:limit="queryParams.pageSize"
      @pagination="getList"
    />
  </ContentWrap>
</template>

<script lang="ts" setup>
import * as OssApi from '@/api/system/ossCapacity'
import {
  formatBytes,
  formatOrderTime,
  payStatusLabel,
  payStatusTagType
} from '@/api/system/ossCapacity'

defineOptions({ name: 'SystemOssCapacityOrders' })

const loading = ref(false)
const list = ref<OssApi.OssCapacityOrderPageItemVO[]>([])
const total = ref(0)
const queryParams = reactive({
  pageNo: 1,
  pageSize: 10,
  tenantId: undefined as string | undefined,
  payStatus: undefined as number | undefined,
  createTime: undefined as string[] | undefined
})

const getList = async () => {
  loading.value = true
  try {
    const tenantIdNum =
      queryParams.tenantId != null && String(queryParams.tenantId).trim() !== ''
        ? Number(queryParams.tenantId)
        : undefined
    const data = await OssApi.getOssCapacityPlatformOrderPage({
      pageNo: queryParams.pageNo,
      pageSize: queryParams.pageSize,
      tenantId: Number.isFinite(tenantIdNum as number) ? tenantIdNum : undefined,
      payStatus: queryParams.payStatus,
      createTime: queryParams.createTime
    })
    list.value = data?.list || []
    total.value = data?.total || 0
  } finally {
    loading.value = false
  }
}

const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

const resetQuery = () => {
  queryParams.tenantId = undefined
  queryParams.payStatus = undefined
  queryParams.createTime = undefined
  handleQuery()
}

onMounted(getList)
</script>

<style scoped>
.text-secondary {
  color: var(--el-text-color-secondary);
  font-size: 12px;
}
</style>
